import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { isNative } from '@ah/native/runtime';
import { getCurrentLocation } from '@ah/native/location';
import { UPDATE_MY_DEVICE_LOCATION_M } from '../shared/queries.ts';
import type { DeviceLocation } from '../shared/types.ts';

const STORAGE_KEY = 'sc_device_location_v1';

type Stored = {
  city: string | null;
  countryCode: string | null;
  updatedAt: string;
};

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Stored;
  } catch {
    return null;
  }
}

function writeStored(s: Stored): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* quota or private mode — fine to silently drop */
  }
}

function clearStored(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow */
  }
}

export function LocationToggle() {
  const gql = useGql();
  const [stored, setStored] = useState<Stored | null>(() => readStored());
  const [error, setError] = useState<string | null>(null);

  // On every Settings mount, re-check whether the OS permission is still
  // granted. If the user revoked it in iOS Settings, drop the stored card.
  useEffect(() => {
    if (!stored) return;
    let cancelled = false;
    void (async () => {
      if (typeof navigator === 'undefined') return;
      if (!isNative() && !navigator.geolocation) {
        if (!cancelled) {
          clearStored();
          setStored(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [stored]);

  const mutation = useMutation({
    mutationFn: async () => {
      const loc = await getCurrentLocation({ highAccuracy: false, timeoutMs: 12_000 });
      if (!loc.ok) throw new Error(loc.reason);
      const { latitude, longitude } = loc.coords;
      const res = await gql.request<{ updateMyDeviceLocation: DeviceLocation }>(
        UPDATE_MY_DEVICE_LOCATION_M,
        { latitude, longitude },
      );
      return res.updateMyDeviceLocation;
    },
    onSuccess: (data) => {
      setError(null);
      const s: Stored = {
        city: data.city,
        countryCode: data.countryCode,
        updatedAt: data.updatedAt,
      };
      writeStored(s);
      setStored(s);
    },
    onError: (err) => {
      // Revert the optimistic flip below.
      clearStored();
      setStored(null);
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[LocationToggle] mutation failed:', err);
      if (msg.includes('permission-denied')) {
        setError('Location access blocked — enable it in Settings to turn this on.');
      } else if (msg.includes('timeout')) {
        setError('Location lookup timed out — try again in a moment.');
      } else {
        // Surface the underlying error rather than swallowing it. GraphQL
        // errors come back as "<HTTP code>: <body>" or similar from the
        // gql client — useful to see what actually failed.
        setError(`Could not save location: ${msg.slice(0, 160)}`);
      }
    },
  });

  const isOn = stored !== null;
  const busy = mutation.isPending;

  function toggle() {
    if (busy) return;
    if (isOn) {
      clearStored();
      setStored(null);
      setError(null);
      return;
    }
    // Optimistic flip — the GPS read + reverse-geocode round-trip takes
    // 2–5 seconds. Placeholder `stored` with null city makes the toggle
    // flip immediately; the subtitle reads "Detecting your location…"
    // because mutation.isPending is true. onError reverts on failure.
    setStored({ city: null, countryCode: null, updatedAt: new Date().toISOString() });
    setError(null);
    mutation.mutate();
  }

  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="flex items-center gap-3">
        <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <MapPin size={18} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Location</p>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle(stored, busy)}</p>
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isOn}
          aria-label="Toggle location sharing"
          onClick={toggle}
          disabled={busy}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            isOn ? 'bg-primary' : 'bg-muted'
          }`}
        >
          <span
            className={`inline-block size-5 transform rounded-full bg-background shadow transition-transform ${
              isOn ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function subtitle(s: Stored | null, busy: boolean): string {
  if (busy) return 'Detecting your location…';
  if (!s) return 'Turn on to prioritise gatherings near you.';
  const where = s.city ?? s.countryCode ?? 'your area';
  return `Location: ${where}; we'll prioritise gatherings near you.`;
}
